import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import CommonTable, { ITableColumn } from '@/components/common/CommonTable';
import FilterComponent from '@/components/common/FilterComponent';
import MainHeading from '@/components/common/MainHeading';
import DeleteIcon from '@/components/Icons/DeleteIcon';
import EditIcon from '@/components/Icons/EditIcon';
import PlusIcon from '@/components/Icons/PlusIcon';
import ViewIcon from '@/components/Icons/ViewIcon';
import CardLayout from '@/components/Layout/CardLayout';
import Layout from '@/components/Layout/Layout';
import AddRegularizationModal from '@/components/Modal/AddRegularizationModal';
import ConfirmModal from '@/components/Modal/ConfirmModal';
import useFilter from '@/hooks/useFilter';
import { IDeleteResponse } from '@/interfaces/common.model';
import {
  IAttendanceRegularizationResponse,
  IRegularizationData,
  IRegularizationFilter,
  IRegularizationModalStatus,
  IRegularizationPageStates,
  IRegularizationTableData,
} from '@/interfaces/regularization.model';
import { RootState } from '@/redux/store';
import {
  getFilterUrl,
  orderByList,
  showToastNotification,
} from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

const MyRegularization = () => {
  const [data, setData] = useState<IRegularizationData>({
    arList: null,
    editArData: null,
  });
  const tableView = useSelector((state: RootState) => state?.user?.tableView);
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const filterDetails = useSelector(
    (state: RootState) => state?.user?.filtersStates,
  );

  const [modalStatus, setModalStatus] = useState<IRegularizationModalStatus>({
    updateData: false,
    deleteData: false,
  });
  const [pageStates, setPageStates] = useState<IRegularizationPageStates>({
    currentPage: 1,
    pageSize: 10,
    count: 0,
    totalPages: 0,
    tableLoader: false,
    searchQuery: '',
  });
  const [filtersStates, setFilterStates] = useState<IRegularizationFilter>({
    filterData: '',
    sortByDate: [
      moment().startOf('month').format('YYYY-MM-DD'),
      moment().endOf('month').format('YYYY-MM-DD'),
    ],
  });
  const tableHeading: ITableColumn<IRegularizationTableData>[] = [
    { value: 'index', label: '#' },
    { value: 'userName', label: 'Name' },
    { value: 'createdOn', label: 'Created On' },
    { value: 'startDate', label: 'Start Date' },
    { value: 'endDate', label: 'End Date' },
    { value: 'checkInTime1', label: 'Check In' },
    { value: 'checkOutTime1', label: 'Check Out' },
    // { value: 'reasonAr', label: 'Reason' },
    { value: 'status', label: 'Status' },
  ];
  const actionData = [
    {
      label: 'View',
      className: 'btn btn-link',
      icon: <ViewIcon />,
      title: 'view',
      isShow: 'always',
      clickHandler: (data: IRegularizationTableData) => {
        navigate(RoutesLink.arRequestDetail + `/${data?.id}`);
      },
    },
    {
      label: 'Edit',
      className: 'btn btn-link',
      icon: <EditIcon />,
      title: 'edit',
      isShow: 0,
      clickHandler: (data: IRegularizationTableData) => handleEdit(data),
    },
    {
      label: 'Delete',
      className: 'btn btn-link',
      icon: <DeleteIcon />,
      title: 'delete',
      isShow: 0,
      clickHandler: (data: IRegularizationTableData) => actionHandler(data),
    },
  ];
  const actionHandler = (data: IRegularizationTableData) => {
    setSelectedId(data?.id);
    toggleModal('delete');
  };
  const toggleModal = (type?: string) => {
    setModalStatus((pre) => ({
      ...pre,
      ...(type === 'delete'
        ? { deleteData: !pre.deleteData }
        : { updateData: !pre.updateData }),
    }));
  };

  const {
    pageChangeHandler,
    handleChangePageSize,
    handleSearchChange,
    orderByHandler,
  } = useFilter(pageStates, setPageStates);
  const dateChangeHandler = (dates: Date[]) => {
    let formattedDate = null;
    if (dates && dates.length === 2) {
      const startDate = new Date(
        dates[0].getTime() - dates[0].getTimezoneOffset() * 60000,
      )
        .toISOString()
        .slice(0, 10);
      const endDate = new Date(
        dates[1].getTime() - dates[1].getTimezoneOffset() * 60000,
      )
        .toISOString()
        .slice(0, 10);
      formattedDate = [startDate, endDate];
    }
    if (formattedDate !== null) {
      setFilterStates((prevState) => ({
        ...prevState,
        sortByDate: formattedDate,
      }));
    } else {
      setFilterStates((prevState) => ({ ...prevState, sortByDate: '' }));
    }
  };
  const handleEdit = (data: IRegularizationTableData) => {
    setData((pre) => ({ ...pre, editArData: data }));
    toggleModal();
  };
  const handleDelete = async () => {
    setIsLoading(true);
    try {
      const response: IDeleteResponse = await callApi(
        API_URL + API_ENDPOINT.deleteAr + `?attendanceId=${selectedId}`,
        {
          method: 'GET',
        },
      );
      if (response?.status) {
        showToastNotification('success', response?.message);
        toggleModal('delete');
        getMyArReq();
      } else {
        showToastNotification('danger', response?.message);
        toggleModal('delete');
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
      toggleModal('delete');
    } finally {
      setIsLoading(false);
    }
  };

  const getMyArReq = async () => {
    setPageStates((pre) => ({
      ...pre,
      tableLoader: !pre.tableLoader,
    }));
    const filter = {
      myArrequest: true,
      search: pageStates.searchQuery,
      orderBy: filterDetails?.orderBy,
      startDate: filtersStates?.sortByDate[0],
      endDate: filtersStates?.sortByDate[1],
    };
    try {
      const response: IAttendanceRegularizationResponse = await callApi(
        API_URL + API_ENDPOINT.getAr + '?' + getFilterUrl(filter),
        {
          method: 'GET',
        },
      );
      if (response?.status) {
        const newData = response?.data?.attendance?.map((item, index) => ({
          ...item,
          index: index,
          startDate: moment(item?.checkInTime).format('DD/MM/YYYY'),
          endDate: moment(item?.checkOutTime).format('DD/MM/YYYY'),
          createdOn: moment(item?.createdAt).format('DD/MM/YYYY'),
          checkInTime1: moment.utc(item?.checkInTime).format('hh:mm A'),
          checkOutTime1:
            item?.checkOutTime &&
            moment.utc(item?.checkOutTime).format('hh:mm A'),
          reasonAr: item?.Arrequests[0]?.reason,
          userName: item?.user?.name,
          workType: item?.Arrequests[0]?.workType,
          status: item?.arRequestStatus,
          document: item?.Arrequests[0]?.document,
        }));
        setData((pre) => ({
          ...pre,
          arList: newData,
        }));

        setPageStates((pre) => ({
          ...pre,
          count: response?.data?.pageInfo?.total ?? 0,
          totalPages: response?.data?.pageInfo?.totalPages ?? 0,
        }));
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setPageStates((pre) => ({
        ...pre,
        tableLoader: !pre.tableLoader,
      }));
    }
  };

  useEffect(() => {
    getMyArReq();
  }, [
    filtersStates?.sortByDate,

    pageStates?.searchQuery,
    pageStates.pageSize,
    filterDetails?.orderBy,
  ]);

  return (
    <Layout>
      <div className="content">
        <MainHeading
          title={'Regularization'}
          buttonIcon={<PlusIcon />}
          buttonText={'Apply Regularization'}
          dateChangeHandler={dateChangeHandler}
          clickHandler={toggleModal}
          showDefaultDate={true}
        />

        <CardLayout isGrid={tableView === 'grid'}>
          <FilterComponent
            isGrid={false}
            // handleFilterForLeave={setPageStates}
            // FilterData={pageStates}
            handleSearch={handleSearchChange}
            orderByHandler={orderByHandler}
            orderByList={orderByList}
          />
          <CommonTable<IRegularizationTableData>
            tableHeading={tableHeading}
            tableData={data?.arList ?? []}
            ActionData={actionData}
            currentPage={pageStates?.currentPage}
            pageChangeHandler={pageChangeHandler}
            isLoading={pageStates?.tableLoader}
            totalPages={pageStates?.totalPages}
            count={pageStates?.count}
            handleChangePageSize={handleChangePageSize}
            pageSize={pageStates.pageSize}
          />
        </CardLayout>
      </div>
      <AddRegularizationModal
        isOpen={modalStatus?.updateData}
        toggle={toggleModal}
        editData={data?.editArData}
        setData={setData}
        getAr={getMyArReq}
      />
      <ConfirmModal
        isOpen={modalStatus.deleteData}
        toggle={() => toggleModal('delete')}
        submitHandler={handleDelete}
        isLoading={isLoading}
      />
    </Layout>
  );
};
export default MyRegularization;
